export const defaultUserOnboarding = {
  business_page_guide_viewed: false,
  billing_page_guide_viewed: false,
  apartments_page_guide_viewed: false,
  single_subscription_page_guide_viewed: false,
  user_info_tasks_guide_viewed: false,
};

let activeOnboardingDriver = null;

export function setActiveOnboardingDriver(driverInstance) {
  activeOnboardingDriver = driverInstance || null;
}

export function destroyActiveOnboardingDriver() {
  if (activeOnboardingDriver && typeof activeOnboardingDriver.destroy === 'function') {
    activeOnboardingDriver.destroy();
    activeOnboardingDriver = null;
  }
}

export function hasSystemDefaultName(user = null) {
  if (!user) {
    return false;
  }

  return `${user.first_name} ${user.last_name}`.trim() === 'Business Owner';
}

export function hasMissingPhoneNumber(user = null) {
  if (!user) {
    return false;
  }

  return !String(user.phone || '').trim();
}

export function getUserOnboardingFlags(user = null, onboardingState = null) {
  if (onboardingState && typeof onboardingState === 'object') {
    return { ...defaultUserOnboarding, ...onboardingState };
  }

  if (user && user.onboarding_flags && typeof user.onboarding_flags === 'object') {
    return { ...defaultUserOnboarding, ...user.onboarding_flags };
  }

  return { ...defaultUserOnboarding };
}

export function shouldShowUserInfoGuide(user = null, onboardingState = null) {
  const flags = getUserOnboardingFlags(user, onboardingState);
  const hasPendingTasks = hasSystemDefaultName(user) || hasMissingPhoneNumber(user);

  return hasPendingTasks && !flags.user_info_tasks_guide_viewed;
}

export function getUserInfoTasks(user = null) {
  const tasks = [];

  if (hasSystemDefaultName(user)) {
    tasks.push({
      id: 'change_name',
      labelKey: 'onboarding.user_info.tasks.change_name',
      routeName: 'user-settings.account',
    });
  }

  if (hasMissingPhoneNumber(user)) {
    tasks.push({
      id: 'add_phone_number',
      labelKey: 'onboarding.user_info.tasks.add_phone_number',
      routeName: 'user-settings.account',
    });
  }

  return tasks;
}

export function getUserInfoOnboardingComputed(user = null, onboardingState = null) {
  return {
    shouldShowUserInfoGuide: shouldShowUserInfoGuide(user, onboardingState),
    hasSystemDefaultName: hasSystemDefaultName(user),
    hasMissingPhoneNumber: hasMissingPhoneNumber(user),
  };
}

export const guideIdByName = {
  businesses: 'businesses',
  billing: 'billing',
  single_subscription: 'single_subscription',
  'single-subscription': 'single_subscription',
  apartments: 'apartments',
  user_info_tasks: 'user_info_tasks',
};

export const guideRuntimeById = {
  businesses: {
    runningStateKey: 'businessGuideRunning',
    setRunningMutation: 'setBusinessGuideRunning',
  },
  billing: {
    runningStateKey: 'billingGuideRunning',
    setRunningMutation: 'setBillingGuideRunning',
  },
  single_subscription: {
    runningStateKey: 'singleSubscriptionGuideRunning',
    setRunningMutation: 'setSingleSubscriptionGuideRunning',
  },
  apartments: {
    runningStateKey: 'apartmentsGuideRunning',
    setRunningMutation: 'setApartmentsGuideRunning',
  },
  user_info_tasks: {
    runningStateKey: 'userInfoTasksGuideRunning',
    setRunningMutation: 'setUserInfoTasksGuideRunning',
  },
};

export function createGuideStartersById(starters = {}) {
  return {
    businesses: starters.startBusinessGuide,
    billing: starters.startBillingGuide,
    single_subscription: starters.startSingleSubscriptionGuide,
    apartments: starters.startApartmentsGuide,
    user_info_tasks: starters.startUserInfoGuide,
  };
}

export function resolveGuideIdByName(guideName = '') {
  const normalizedGuideName = String(guideName || '').trim().toLowerCase();
  return guideIdByName[normalizedGuideName] || null;
}

export function createOnboardingStoreParts(drivers = {}) {
  const guideStartersById = createGuideStartersById({
    startBusinessGuide: drivers.startBusinessGuide,
    startBillingGuide: drivers.startBillingGuide,
    startSingleSubscriptionGuide: drivers.startSingleSubscriptionGuide,
    startApartmentsGuide: drivers.startApartmentsGuide,
    startUserInfoGuide: drivers.startUserInfoGuide,
  });

  return {
    state: {
      userOnboarding: { ...defaultUserOnboarding },
      introModalType: null,
      businessBillingOnboardingFlowActive: false,
      businessGuideCompletedInFlow: false,
      billingGuideCompletedInFlow: false,
      businessGuideRunning: false,
      billingGuideRunning: false,
      singleSubscriptionGuideRunning: false,
      apartmentsGuideRunning: false,
      userInfoTasksGuideRunning: false,
    },
    mutations: {
      setUserOnboarding(state, userOnboarding) {
        state.userOnboarding = { ...defaultUserOnboarding, ...userOnboarding };
      },
      setIntroModalType(state, type) {
        state.introModalType = type;
      },
      setBusinessBillingOnboardingFlowActive(state, active) {
        state.businessBillingOnboardingFlowActive = active;
      },
      setBusinessGuideCompletedInFlow(state, value) {
        state.businessGuideCompletedInFlow = value;
      },
      setBillingGuideCompletedInFlow(state, value) {
        state.billingGuideCompletedInFlow = value;
      },
      setBusinessGuideRunning(state, value) {
        state.businessGuideRunning = value;
      },
      setBillingGuideRunning(state, value) {
        state.billingGuideRunning = value;
      },
      setSingleSubscriptionGuideRunning(state, value) {
        state.singleSubscriptionGuideRunning = value;
      },
      setApartmentsGuideRunning(state, value) {
        state.apartmentsGuideRunning = value;
      },
      setUserInfoTasksGuideRunning(state, value) {
        state.userInfoTasksGuideRunning = value;
      },
      clearOnboardingState(state) {
        state.userOnboarding = { ...defaultUserOnboarding };
        state.introModalType = null;
        state.businessBillingOnboardingFlowActive = false;
        state.businessGuideCompletedInFlow = false;
        state.billingGuideCompletedInFlow = false;
        state.businessGuideRunning = false;
        state.billingGuideRunning = false;
        state.singleSubscriptionGuideRunning = false;
        state.apartmentsGuideRunning = false;
        state.userInfoTasksGuideRunning = false;
      },
    },
    actions: {
      updateUserOnboardingFlags({ commit }, flags) {
        return drivers.axios
          .post('user-settings/onboarding-flags', { flags })
          .then(({ data }) => {
            if (data && data.onboarding_flags) {
              commit('setUserOnboarding', data.onboarding_flags);
            }
            return data;
          });
      },
      closeIntroModal({ state, commit, dispatch }, { markAsViewed = true } = {}) {
        const currentIntroModalType = state.introModalType;
        commit('setIntroModalType', null);

        if (!markAsViewed) {
          return Promise.resolve();
        }

        if (currentIntroModalType === 'business_billing') {
          commit('setBusinessBillingOnboardingFlowActive', false);
          return dispatch('updateUserOnboardingFlags', {
            business_page_guide_viewed: true,
            billing_page_guide_viewed: true,
          });
        }

        if (currentIntroModalType === 'user_info') {
          return dispatch('updateUserOnboardingFlags', {
            user_info_tasks_guide_viewed: true,
          });
        }

        return Promise.resolve();
      },
      startBusinessBillingGuideSequence({ getters, commit, dispatch }, { router }) {
        if (getters.isAdmin) {
          return Promise.resolve(false);
        }
        commit('setIntroModalType', null);
        commit('setBusinessBillingOnboardingFlowActive', true);
        commit('setBusinessGuideCompletedInFlow', false);
        commit('setBillingGuideCompletedInFlow', false);
        return dispatch('startBusinessBillingGuideFlow', {
          routeName: router.currentRoute.name,
          router,
        });
      },
      startBusinessBillingGuideFlow(
        {
          state, getters, commit, dispatch,
        },
        { routeName, router, categoryConst } = {},
      ) {
        if (!state.user || getters.isAdmin) {
          return Promise.resolve(false);
        }

        const isApartmentsRoute = routeName === 'Ads' && categoryConst === 'apartment';
        if (isApartmentsRoute && !state.userOnboarding.apartments_page_guide_viewed) {
          return Promise.resolve(false);
        }

        const isSingleSubscriptionRoute = routeName === 'my-subscriptions.subscription-single';
        if (isSingleSubscriptionRoute && !state.userOnboarding.single_subscription_page_guide_viewed) {
          return Promise.resolve(false);
        }

        const hasBusinesses = Number(state.user.businessesCount || 0) > 0;
        const shouldRunBusinessGuide = hasBusinesses && !state.userOnboarding.business_page_guide_viewed;
        const shouldRunBillingGuide = hasBusinesses && !state.userOnboarding.billing_page_guide_viewed;
        const shouldRunFlow = shouldRunBusinessGuide || shouldRunBillingGuide;

        if (!shouldRunFlow) {
          if (state.introModalType === 'business_billing') {
            commit('setIntroModalType', null);
          }
          commit('setBusinessBillingOnboardingFlowActive', false);
          return Promise.resolve(false);
        }

        if (!state.businessBillingOnboardingFlowActive) {
          commit('setIntroModalType', 'business_billing');
          return Promise.resolve(false);
        }

        if (shouldRunBusinessGuide && !state.businessGuideCompletedInFlow) {
          if (routeName !== 'Businesses') {
            router.push({ name: 'Businesses' });
            return Promise.resolve(false);
          }

          if (state.businessGuideRunning) {
            return Promise.resolve(false);
          }

          commit('setBusinessGuideRunning', true);
          return drivers.startBusinessGuide({
            onFinish: () => {
              commit('setBusinessGuideRunning', false);
              commit('setBusinessGuideCompletedInFlow', true);
              router.push({ name: 'my-subscriptions' });
            },
            onDeactivated: () => {
              commit('setBusinessGuideRunning', false);
              commit('setBusinessBillingOnboardingFlowActive', false);
            },
          }).then((started) => {
            if (!started) {
              commit('setBusinessGuideRunning', false);
            }
            return started;
          });
        }

        if (shouldRunBillingGuide && !state.billingGuideCompletedInFlow) {
          if (routeName !== 'my-subscriptions') {
            router.push({ name: 'my-subscriptions' });
            return Promise.resolve(false);
          }

          if (state.billingGuideRunning) {
            return Promise.resolve(false);
          }

          commit('setBillingGuideRunning', true);
          return drivers.startBillingGuide({
            onFinish: () => {
              commit('setBillingGuideRunning', false);
              commit('setBillingGuideCompletedInFlow', true);
              dispatch('updateUserOnboardingFlags', {
                business_page_guide_viewed: true,
                billing_page_guide_viewed: true,
              }).finally(() => {
                commit('setBusinessBillingOnboardingFlowActive', false);
                dispatch('startUserInfoGuide', { routeName: router.currentRoute.name });
              });
            },
            onDeactivated: () => {
              commit('setBillingGuideRunning', false);
              commit('setBusinessBillingOnboardingFlowActive', false);
            },
          }).then((started) => {
            if (!started) {
              commit('setBillingGuideRunning', false);
            }
            return started;
          });
        }

        commit('setBusinessBillingOnboardingFlowActive', false);
        return Promise.resolve(false);
      },
      startUserInfoGuide(
        {
          state, getters, commit, dispatch,
        },
        { routeName, categoryConst } = {},
      ) {
        if (!state.user || getters.isAdmin) {
          return Promise.resolve(false);
        }

        const isApartmentsRoute = routeName === 'Ads' && categoryConst === 'apartment';
        if (isApartmentsRoute && !state.userOnboarding.apartments_page_guide_viewed) {
          if (state.introModalType === 'user_info') {
            commit('setIntroModalType', null);
          }
          return Promise.resolve(false);
        }

        const isSingleSubscriptionRoute = routeName === 'my-subscriptions.subscription-single';
        if (isSingleSubscriptionRoute && !state.userOnboarding.single_subscription_page_guide_viewed) {
          if (state.introModalType === 'user_info') {
            commit('setIntroModalType', null);
          }
          return Promise.resolve(false);
        }

        const hasBusinesses = Number(state.user.businessesCount || 0) > 0;
        const hasPendingBusinessBillingFlow = hasBusinesses
          && (!state.userOnboarding.business_page_guide_viewed || !state.userOnboarding.billing_page_guide_viewed);
        if (hasPendingBusinessBillingFlow) {
          if (state.introModalType === 'user_info') {
            commit('setIntroModalType', null);
          }
          return Promise.resolve(false);
        }

        const shouldShow = shouldShowUserInfoGuide(state.user, state.userOnboarding);
        if (!shouldShow) {
          if (state.introModalType === 'user_info') {
            commit('setIntroModalType', null);
          }
          return Promise.resolve(false);
        }

        if (routeName !== 'user-settings.account') {
          commit('setIntroModalType', 'user_info');
          return Promise.resolve(false);
        }

        if (state.introModalType === 'user_info') {
          commit('setIntroModalType', null);
        }

        return drivers.startUserInfoGuide({
          hasSystemDefaultName: hasSystemDefaultName(state.user),
          hasMissingPhoneNumber: hasMissingPhoneNumber(state.user),
          onFinish: () => {
            dispatch('updateUserOnboardingFlags', { user_info_tasks_guide_viewed: true });
          },
          onDeactivated: () => {
            commit('setUserInfoTasksGuideRunning', false);
          },
        });
      },
      startSingleSubscriptionGuideFlow(
        {
          state, getters, commit, dispatch,
        },
        { routeName } = {},
      ) {
        if (
          !state.user
          || getters.isAdmin
          || routeName !== 'my-subscriptions.subscription-single'
          || state.userOnboarding.single_subscription_page_guide_viewed
          || state.singleSubscriptionGuideRunning
        ) {
          return Promise.resolve(false);
        }

        commit('setSingleSubscriptionGuideRunning', true);
        return drivers.startSingleSubscriptionGuide({
          onFinish: () => {
            commit('setSingleSubscriptionGuideRunning', false);
            dispatch('updateUserOnboardingFlags', {
              single_subscription_page_guide_viewed: true,
            });
          },
          onDeactivated: () => {
            commit('setSingleSubscriptionGuideRunning', false);
          },
        }).then((started) => {
          if (!started) {
            commit('setSingleSubscriptionGuideRunning', false);
          }
          return started;
        });
      },
      startApartmentsGuideFlow(
        {
          state, getters, commit, dispatch,
        },
        { routeName, categoryConst } = {},
      ) {
        if (
          !state.user
          || getters.isAdmin
          || routeName !== 'Ads'
          || categoryConst !== 'apartment'
          || state.userOnboarding.apartments_page_guide_viewed
          || state.apartmentsGuideRunning
        ) {
          return Promise.resolve(false);
        }

        commit('setApartmentsGuideRunning', true);
        return drivers.startApartmentsGuide({
          onFinish: () => {
            commit('setApartmentsGuideRunning', false);
            dispatch('updateUserOnboardingFlags', {
              apartments_page_guide_viewed: true,
            });
          },
          onDeactivated: () => {
            commit('setApartmentsGuideRunning', false);
          },
        }).then((started) => {
          if (!started) {
            commit('setApartmentsGuideRunning', false);
          }
          return started;
        });
      },
      startGuideByName({ state, commit }, { guideName } = {}) {
        const guideId = resolveGuideIdByName(guideName);
        const startGuide = guideStartersById[guideId];
        const guideRuntime = guideRuntimeById[guideId];

        if (!startGuide || !guideRuntime || state[guideRuntime.runningStateKey]) {
          return Promise.resolve(false);
        }

        commit(guideRuntime.setRunningMutation, true);
        return startGuide({
          onFinish: () => {
            commit(guideRuntime.setRunningMutation, false);
          },
          onDeactivated: () => {
            commit(guideRuntime.setRunningMutation, false);
          },
        }).then((started) => {
          if (!started) {
            commit(guideRuntime.setRunningMutation, false);
          }
          return started;
        });
      },
    },
    getters: {
      userOnboarding: (state) => state.userOnboarding,
      introModalType: (state) => state.introModalType,
      showIntroModal: (state) => Boolean(state.introModalType),
    },
  };
}
